/**
 * Outputs: what a project has made, as one list. Documents (artifacts), concept
 * ideas and the pictures the agents generated, newest first. Uploaded pictures
 * are material, not outputs, so they stay out.
 *
 * Honesty rules while the backend catches up:
 * - A kind tag shows only when the data carries a type. Documents are all
 *   "markdown" until creative-os-backend#114 gives each one its own type, so a
 *   document has no tag rather than a guessed one. Ideas and pictures are
 *   their own records, so IDEA and IMAGE are facts.
 * - Every count comes from what was actually read. A project whose outputs
 *   failed to load has no output count, never a zero.
 */
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { POLL_CURATION_MS, clampWhenHidden } from "@/lib/realtime";
import type {
  WorkshopArtifactListItem,
  WorkshopIdeaListItem,
} from "@/types/workshop.type";
import { workshopArtifactsQuery } from "./api/useWorkshopArtifacts";
import { workshopIdeasQuery } from "./api/useWorkshopIdeas";
import { workshopMediaQuery, type WorkshopMediaItem } from "./api/useWorkshopMedia";

export type WorkshopOutputKind = "document" | "idea" | "image";

export type WorkshopOutput = {
  /** `${kind}:${id}`, unique across kinds. */
  key: string;
  kind: WorkshopOutputKind;
  id: string;
  projectId: string;
  title: string;
  /** The kind tag, only when the data really carries a type (see above). */
  tag: string | null;
  /** Documents only: the working version. */
  version: number | null;
  /** A state worth naming (an idea already on the calendar). */
  state: string | null;
  /** When it was made. */
  madeAt: string;
  /** When it last changed: the list's order. */
  at: string;
  /** Pictures only: the small image to show beside the row. */
  thumbUrl: string | null;
};

/** Artifact kinds that name a file format, not a type of document. */
const UNTYPED_DOCUMENT_KINDS = new Set(["markdown", "document", "text", ""]);

export function documentTag(kind: string): string | null {
  const k = (kind ?? "").trim().toLowerCase();
  if (UNTYPED_DOCUMENT_KINDS.has(k)) return null;
  return k.replaceAll("_", " ").toUpperCase();
}

export function outputFromArtifact(
  projectId: string,
  a: WorkshopArtifactListItem,
): WorkshopOutput {
  return {
    key: `document:${a.id}`,
    kind: "document",
    id: a.id,
    projectId,
    title: a.title.trim() || "Untitled document",
    tag: documentTag(a.kind),
    version: a.version,
    state: null,
    madeAt: a.attribution?.createdBy.at ?? a.updatedAt,
    at: a.updatedAt,
    thumbUrl: null,
  };
}

export function outputFromIdea(projectId: string, i: WorkshopIdeaListItem): WorkshopOutput {
  return {
    key: `idea:${i.id}`,
    kind: "idea",
    id: i.id,
    projectId,
    title: i.title.trim() || "Untitled idea",
    tag: "IDEA",
    version: null,
    state: i.status === "MIGRATED" ? "ON CALENDAR" : null,
    madeAt: i.attribution?.createdBy.at ?? i.updatedAt,
    at: i.updatedAt,
    thumbUrl: null,
  };
}

/**
 * A picture is an output when an agent made it here (and it has landed).
 * Uploads and library copies are material, even a library picture that was
 * itself AI-made: it was brought in, not made in this project, so the room
 * lists it under Context. One rule for the Projects home, the room and the
 * counts beside the thread head.
 */
export function isOutputPicture(m: WorkshopMediaItem): boolean {
  return m.isAiGenerated && !m.pending && m.library?.relation !== "copied_from";
}

export function outputFromMedia(projectId: string, m: WorkshopMediaItem): WorkshopOutput {
  return {
    key: `image:${m.id}`,
    kind: "image",
    id: m.id,
    projectId,
    title: m.fileName?.trim() || "Generated picture",
    tag: "IMAGE",
    version: null,
    state: null,
    madeAt: m.createdAt,
    at: m.createdAt,
    thumbUrl: m.thumbUrl ?? m.url ?? null,
  };
}

/** One project's outputs from whatever lists it has, newest first. */
export function projectOutputs(
  projectId: string,
  artifacts: WorkshopArtifactListItem[] | undefined,
  ideas: WorkshopIdeaListItem[] | undefined,
  media: WorkshopMediaItem[] | undefined,
): WorkshopOutput[] {
  const out: WorkshopOutput[] = [];
  for (const a of artifacts ?? []) {
    if (a.status === "archived") continue;
    out.push(outputFromArtifact(projectId, a));
  }
  for (const i of ideas ?? []) {
    if (i.status === "ARCHIVED") continue;
    out.push(outputFromIdea(projectId, i));
  }
  for (const m of media ?? []) {
    // Only what an agent made. An upload, or a copy from the library, is
    // material the project works from.
    if (!isOutputPicture(m)) continue;
    out.push(outputFromMedia(projectId, m));
  }
  return sortNewestFirst(out);
}

export function sortNewestFirst<T extends WorkshopOutput>(outputs: T[]): T[] {
  return [...outputs].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

const DAY_MS = 24 * 60 * 60 * 1000;

function isToday(date: Date, now: Date): boolean {
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

/** "16:00" today, "Sep 24" this year, "Sep 24, 2025" before. */
export function formatMadeDate(iso: string, now = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  if (isToday(date, now)) {
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/**
 * The mono line beside an output: the version (documents), NEW for a picture
 * or idea made in the last day, a state worth naming, then the date.
 */
export function outputMeta(output: WorkshopOutput, now = new Date()): string {
  const parts: string[] = [];
  if (output.version != null) parts.push(`V${output.version}`);
  else if (now.getTime() - Date.parse(output.madeAt) < DAY_MS) parts.push("NEW");
  if (output.state) parts.push(output.state);
  const date = formatMadeDate(output.at, now);
  if (date) parts.push(date);
  return parts.join(" · ");
}

function titleKey(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Outputs that share a title with a newer one in the same list. The newest of
 * a set carries no flag; each older one says it has a twin.
 */
export function sameTitleKeys(outputs: WorkshopOutput[]): Set<string> {
  const seen = new Set<string>();
  const flagged = new Set<string>();
  for (const output of sortNewestFirst(outputs)) {
    const key = titleKey(output.title);
    if (!key) continue;
    if (seen.has(key)) flagged.add(output.key);
    else seen.add(key);
  }
  return flagged;
}

export type ProjectOutputsState = {
  /** What loaded, newest first. May be partial when `failed`. */
  outputs: WorkshopOutput[];
  /** Every read for this project answered. */
  ready: boolean;
  /** At least one read for this project failed; `outputs` holds the rest. */
  failed: boolean;
  loading: boolean;
};

const EMPTY_STATE: ProjectOutputsState = {
  outputs: [],
  ready: false,
  failed: false,
  loading: true,
};

/**
 * Every listed project's outputs, read per project through the same keys the
 * project room uses (there is no brand-wide outputs route; searching across
 * projects is creative-os-backend#116). Documents and ideas poll slowly as a
 * net; pictures never poll, because each row carries a presigned URL.
 */
export function useProjectsOutputs(projectIds: string[]) {
  const results = useQueries({
    queries: projectIds.flatMap((id) => [
      {
        ...workshopArtifactsQuery(id, false),
        refetchInterval: () => clampWhenHidden(POLL_CURATION_MS),
      },
      {
        ...workshopIdeasQuery(id, false),
        refetchInterval: () => clampWhenHidden(POLL_CURATION_MS),
      },
      workshopMediaQuery(id),
    ]),
  });

  const dataKey = results
    .map((r) => `${r.status}:${r.dataUpdatedAt}:${r.errorUpdatedAt}`)
    .join("|");

  return useMemo(() => {
    const byProject = new Map<string, ProjectOutputsState>();
    projectIds.forEach((id, index) => {
      const [artifacts, ideas, media] = [
        results[index * 3],
        results[index * 3 + 1],
        results[index * 3 + 2],
      ];
      if (!artifacts || !ideas || !media) {
        byProject.set(id, EMPTY_STATE);
        return;
      }
      const parts = [artifacts, ideas, media];
      const failed = parts.some((q) => q.isError && q.data === undefined);
      const loading = parts.some((q) => q.status === "pending");
      byProject.set(id, {
        outputs: projectOutputs(
          id,
          artifacts.data as WorkshopArtifactListItem[] | undefined,
          ideas.data as WorkshopIdeaListItem[] | undefined,
          media.data as WorkshopMediaItem[] | undefined,
        ),
        ready: parts.every((q) => q.data !== undefined),
        failed,
        loading,
      });
    });

    const states = [...byProject.values()];
    const all = sortNewestFirst(states.flatMap((s) => s.outputs));
    const failedIds = projectIds.filter((id) => byProject.get(id)?.failed);
    return {
      byProject,
      all,
      /** Every project's outputs are in: search can promise outputs. */
      allReady: states.length > 0 && states.every((s) => s.ready),
      anyLoading: states.some((s) => s.loading),
      failedIds,
      retry: () => {
        results.forEach((r) => {
          if (r.isError) void r.refetch();
        });
      },
    };
    // `dataKey` stands in for `results`, whose identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, projectIds.join("|")]);
}
